import { useEffect } from 'react'
import { liveTuneStore } from '../lib/liveTuneStore'
import { environmentPartsFor } from '../lib/environmentFiles'

/**
 * Keep `liveTuneStore.environment.parts` in step with what the SERVER says the
 * active environment is made of (ENG-1132).
 *
 * ── Why this is not optional ────────────────────────────────────────────────
 * An environment's parts are held ephemerally, like its asset id and for the
 * same reason: they are server-derived, not a setting anyone chose. So a page
 * reload restores `splatUrl` from localStorage and NOTHING else, and a
 * four-part room would come back as its walls alone — no error, no empty state,
 * just a room quietly missing its furniture, which is the silent-fallback
 * failure the UX rules exist to prevent.
 *
 * Persisting the parts instead would trade that for a worse one: a stale local
 * copy winning over the server's, which is precisely the bug the
 * write-through-cache rule was written after.
 *
 * So the server is asked on every load. It costs no extra request — the
 * environment list is already fetched to resolve the asset id, and
 * environmentPartsFor rides that same lookup.
 *
 * ── What it does NOT do ─────────────────────────────────────────────────────
 * It does not clear the parts while it is resolving. applyEnvironment writes
 * them synchronously when a human picks an environment, and blanking them for
 * one async tick would unmount every prop and re-download it. The base splat is
 * mounted under its own URL in both cases, so it survives the transition
 * without re-downloading (see partMounts' key rule).
 *
 * A failed lookup leaves whatever is in the store and says so. Writing [] there
 * would render a multipart room as its walls and call it success.
 */
export function useEnvironmentParts(splatUrl) {
  useEffect(() => {
    if (!splatUrl) return
    let cancelled = false
    environmentPartsFor(splatUrl)
      .then(parts => {
        if (cancelled) return
        // Compare before writing: the store notifies synchronously, and a
        // no-op write on every environment resolve would re-render the whole
        // scene for nothing.
        const current = liveTuneStore.getState()?.environment?.parts ?? []
        if (samePartList(current, parts)) return
        liveTuneStore.setStateEphemeral({ environment: { parts } })
      })
      .catch(err => {
        console.error(
          `[useEnvironmentParts] could not resolve the parts of ${splatUrl} — ` +
          `leaving what is loaded rather than rendering it as a single splat:`, err,
        )
      })
    return () => { cancelled = true }
  }, [splatUrl])
}

/** Identity of two part lists for the purposes of "do we need to re-render" —
 *  names and URLs, which is what decides what gets mounted. */
function samePartList(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.name !== b[i]?.name || a[i]?.url !== b[i]?.url) return false
  }
  return true
}
